import * as breakpoints from "../../breakpoints";
import * as classTileColors from "../../constants/classTileColors";
import * as languages from "../../constants/languages";
import { css, StyleSheet } from "aphrodite";
import categoryColors from "../../constants/categoryColors";
import { getBookmarks } from "../../api/bookmark";
import { getCertificates } from "../../api/certificate";
import { getInProgressClasses } from "../../api/class";
import { english, spanish } from "../../constants/strings";
import { handleUnknownError } from "../../lib/errorHandler";
import CertificateModal from "../CertificateModal";
import CoursesContainer from "../CoursesContainer";
import ClassesFilterContainer from "../ClassesFilterContainer";
import Page from "../Page";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import { uniqueObjectsByKey } from "../../lib/util";

const styles = StyleSheet.create({
	content: {
		"@media print": {
			display: "none",
		},
	},

	noScrollContent: {
		[breakpoints.mobileOnly]: {
			maxHeight: "50vh",
			overflow: "hidden",
		},
	},
});

export default class MyClassesPage extends PureComponent {
	static propTypes = {
		language: PropTypes.string.isRequired,
		pathname: PropTypes.string.isRequired,
	};

	constructor(props) {
		super(props);

		this.strings = props.language === languages.es ? spanish : english;

		this.filterOptions = [
			{ value: "IN-PROGRESS", label: this.strings.IN_PROGRESS },
			{ value: "BOOKMARKED", label: this.strings.BOOKMARKED },
			{ value: "CERTIFICATES", label: this.strings.CERTIFICATES },
		];

		this.state = {
			selectedFilter: this.filterOptions[0].value,
			showCertificate: false,
			certificateCourse: {},
			certificateId: "",
			categories: [],
			bookmarkedClasses: [],
			certificateClasses: [],
			inProgressClasses: [],
		};
	}

	componentDidMount = () => {
		this._loadCourses();
	};

	_loadCourses = () => {
		getBookmarks(this.props.language)
			.then(data => {
				this.setState({
					bookmarkedClasses: data.classes,
					categories: [
						...this.state.categories,
						...uniqueObjectsByKey(data.classes, "primaryCategoryName"),
					],
				});
			})
			.catch(err => {
				handleUnknownError(err);
			});
		getCertificates(this.props.language)
			.then(certificates => {
				this.setState({
					certificateClasses:
						certificates.total > 0
							? certificates.certificates.map(cert => ({
									...cert.class,
									complete: true,
									isCertificate: true,
									certificate: cert.id,
									completionDate: cert.completionDate,
								}))
							: [],
					categories: [
						...this.state.categories,
						...uniqueObjectsByKey(
							certificates.certificates.map(cert => cert.class),
							"primaryCategoryName",
						),
					],
				});
			})
			.catch(err => {
				handleUnknownError(err);
			});
		getInProgressClasses(this.props.language)
			.then(inProgressClasses => {
				this.setState({
					inProgressClasses: inProgressClasses.classes.map(c => {
						return {
							...c,
							inProgress: true,
						};
					}),
					categories: [
						...this.state.categories,
						...uniqueObjectsByKey(inProgressClasses.classes, "primaryCategoryName"),
					],
				});
			})
			.catch(err => {
				handleUnknownError(err);
			});
	};

	_filteredCourses = () => {
		if (this.state.selectedFilter === this.filterOptions[0].value) {
			return this.state.inProgressClasses;
		} else if (this.state.selectedFilter === this.filterOptions[1].value) {
			return this.state.bookmarkedClasses;
		} else if (this.state.selectedFilter === this.filterOptions[2].value) {
			return this.state.certificateClasses;
		} else {
			return [];
		}
	};

	_setFilter = selectedFilter => {
		this.setState({
			selectedFilter,
		});
	};

	_showCertificate = (course, certificateId) => {
		this.setState({
			showCertificate: true,
			certificateCourse: course || {},
			certificateId: certificateId || "",
		});
	};

	_hideCertificate = () => {
		this.setState({
			showCertificate: false,
		});
	};

	_updateBookmark = course => {
		const inProgressClasses = this.state.inProgressClasses.map(c => {
			if (c.id === course.id) {
				return {
					...c,
					bookmarked: course.bookmarked,
				};
			}
			return { ...c };
		});
		const certificateClasses = this.state.certificateClasses.map(c => {
			if (c.id === course.id) {
				return {
					...c,
					bookmarked: course.bookmarked,
				};
			}
			return { ...c };
		});
		// add class if it isn't there
		if (!course.bookmarked) {
			const bookmarkedClasses = this.state.bookmarkedClasses.filter(c => c.id !== course.id);

			this.setState({
				certificateClasses,
				inProgressClasses,
				bookmarkedClasses,
			});
		} else {
			const bookmarkedClasses = uniqueObjectsByKey(
				this.state.bookmarkedClasses.concat(course),
				"id",
			);
			this.setState({
				certificateClasses,
				inProgressClasses,
				bookmarkedClasses,
			});
		}
	};

	_categoryColors = () => {
		const catColors = categoryColors;
		const colors = Object.keys(classTileColors).map(colorName => classTileColors[colorName]);
		let colorIndex = Object.keys(categoryColors).length;
		this.state.categories.forEach(opt => {
			if (
				!(
					opt.primaryCategoryName
						.toLowerCase()
						.replace(/[&/\\#,+()$~%.'":*?<>{}\s’]/g, "") in catColors
				)
			) {
				catColors[
					opt.primaryCategoryName
						.toLowerCase()
						.replace(/[&/\\#,+()$~%.'":*?<>{}\s’]/g, "")
				] =
					colors[colors.length > colorIndex ? colorIndex : colorIndex - colors.length];
				colorIndex += 1;
			}
		});
		return catColors;
	};

	render() {
		return (
			<Page
				activeTab={this.strings.tabNames.MY_CLASSES}
				language={this.props.language}
				pageTitle={this.strings.tabNames.MY_CLASSES}
				currentRoute={this.props.pathname}
				modalShowing={this.state.showCertificate}
			>
				<div>
					<div
						className={css(
							this.state.showCertificate
								? [styles.noScrollContent, styles.content]
								: styles.content,
						)}
					>
						<ClassesFilterContainer
							selectFilter={this._setFilter}
							selectedFilter={this.state.selectedFilter}
							options={this.filterOptions}
						/>
						<CoursesContainer
							categoryColors={this._categoryColors()}
							courses={this._filteredCourses()}
							filter={this.state.selectedFilter}
							language={this.props.language}
							showCertificate={this._showCertificate}
							updateBookmark={this._updateBookmark}
						/>
					</div>
					<CertificateModal
						showing={this.state.showCertificate}
						classId={this.state.certificateCourse.id}
						certificate={this.state.certificateId}
						dismiss={this._hideCertificate}
						language={this.props.language}
					/>
				</div>
			</Page>
		);
	}
}



// WEBPACK FOOTER //
// ./src/components/pages/MyClassesPage.js